import React, { useEffect, useState } from 'react';
import {
  Button,
  DialogContent,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import styles from './AddManagerDialog.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import { isValidEmail } from '../../utils/utils';
import InputField from '../atoms/InputField';
import { useSessionData } from '../../providers';

import type { Manager, WithOptionalId } from '../../types';

export interface AddManagerDialogProps {
  onClose: () => void;
  onSubmit: (
    newManager: WithOptionalId<Manager>,
  ) => Promise<{ success: boolean; emailAlreadyInUse?: boolean }>;
  isOpen: boolean;
}

const initialManagerData = {
  firstName: '',
  lastName: '',
  email: '',
  emailVerification: '',
  subscription: '',
};

const AddManagerDialog: React.FC<AddManagerDialogProps> = ({
  isOpen,
  onSubmit,
  onClose,
}) => {
  const { gym } = useSessionData();
  const [managerData, setManagerData] = useState(initialManagerData);

  const [firstNameError, setFirstNameError] = useState<boolean>(false);
  const [lastNameError, setLastNameError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
  const [emailVerificationError, setEmailVerificationError] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setManagerData(initialManagerData);
      setFirstNameError(false);
      setLastNameError(false);
      setEmailError(false);
      setEmailErrorMessage('');
      setEmailVerificationError(false);
      setLoading(false);
      setErrorMessage(null);
      setIsCloseConfirmationOpen(false);
    }
  }, [isOpen]);

  const hasChanges = () =>
    managerData.firstName !== initialManagerData.firstName ||
    managerData.lastName !== initialManagerData.lastName ||
    managerData.email !== initialManagerData.email ||
    managerData.emailVerification !== initialManagerData.emailVerification;

  const handleRequestClose = () => {
    if (hasChanges()) {
      setIsCloseConfirmationOpen(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    setIsCloseConfirmationOpen(false);
    onClose();
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    const normalizedEmail = managerData.email.trim();
    const normalizedEmailVerification = managerData.emailVerification.trim();
    const isEmailInvalid = !isValidEmail(normalizedEmail);
    const validationErrors = {
      firstName: !managerData.firstName,
      lastName: !managerData.lastName,
      email: isEmailInvalid,
      emailVerification: normalizedEmail !== normalizedEmailVerification,
    };

    setFirstNameError(validationErrors.firstName);
    setLastNameError(validationErrors.lastName);
    setEmailError(validationErrors.email);
    setEmailErrorMessage(
      validationErrors.email ? 'Please enter a valid email address.' : '',
    );
    setEmailVerificationError(validationErrors.emailVerification);

    if (Object.values(validationErrors).some(Boolean)) return;

    setLoading(true);

    const newManager: WithOptionalId<Manager> = {
      email: normalizedEmail,
      firstName: managerData.firstName,
      lastName: managerData.lastName,
      gymIds: [gym?.id || ''],
      isActive: true,
      isAdmin: false,
    };

    try {
      const result = await onSubmit(newManager);

      if (result.success) {
        setManagerData(initialManagerData);
        onClose();
      } else if (result.emailAlreadyInUse) {
        setEmailError(true);
        setEmailErrorMessage('Email already in use.');
      } else {
        setErrorMessage('An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <BaseDialog
        onClose={handleRequestClose}
        isOpen={isOpen}
        title="Add A Manager"
        actions={
          <Button
            className={styles.submit}
            variant="outlined"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="primary" />
            ) : (
              <>
                <AddBoxOutlinedIcon color="primary" />
                <span className={styles.submitText}>Add Manager</span>
              </>
            )}
          </Button>
        }
      >
        <DialogContent className={styles.managerDataDialog}>
          <InputField
            icon={<PersonOutlineOutlinedIcon color="primary" />}
            title="First Name"
            inputName="firstName"
            autoComplete="firstName"
            value={managerData.firstName}
            onChange={(firstName) => {
              setFirstNameError(false);
              setManagerData((prevManagerData) => ({
                ...prevManagerData,
                firstName,
              }));
            }}
            error={firstNameError}
            validationMessage="Please enter a first name."
          />
          <InputField
            icon={<PersonOutlineOutlinedIcon color="primary" />}
            title="Last Name"
            inputName="lastName"
            autoComplete="lastName"
            value={managerData.lastName}
            onChange={(lastName) => {
              setLastNameError(false);
              setManagerData((prevManagerData) => ({
                ...prevManagerData,
                lastName,
              }));
            }}
            error={lastNameError}
            validationMessage="Please enter a last name."
          />
          <InputField
            icon={<MailOutlineRoundedIcon color="primary" />}
            title="Email"
            inputName="email"
            autoComplete="email"
            value={managerData.email}
            onChange={(email) => {
              setEmailError(false);
              setEmailErrorMessage('');
              setEmailVerificationError(false);
              setManagerData((prevManagerData) => ({
                ...prevManagerData,
                email,
              }));
            }}
            onBlur={() => {
              const isInvalid = !isValidEmail(managerData.email);
              setEmailError(isInvalid);
              setEmailErrorMessage(
                isInvalid ? 'Please enter a valid email address.' : '',
              );
            }}
            error={emailError}
            validationMessage={emailErrorMessage}
          />
          <InputField
            icon={<MailOutlineRoundedIcon color="primary" />}
            title="Email Verification"
            inputName="emailVerification"
            autoComplete="email"
            value={managerData.emailVerification}
            onChange={(emailVerification) => {
              setEmailVerificationError(false);
              setManagerData((prevManagerData) => ({
                ...prevManagerData,
                emailVerification,
              }));
            }}
            onBlur={() =>
              setEmailVerificationError(
                managerData.email !== managerData.emailVerification,
              )
            }
            error={emailVerificationError}
            validationMessage="Emails do not match."
          />
        </DialogContent>
      </BaseDialog>
      <ConfirmationDialog
        isOpen={isCloseConfirmationOpen}
        onCancel={() => setIsCloseConfirmationOpen(false)}
        onSubmit={() => confirmClose()}
        title="Discard changes?"
        text="You have unsaved changes. Do you want to discard them?"
        submitText="Discard"
        cancelText="Keep Editing"
      />
      {errorMessage && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={() => setErrorMessage(null)}
        >
          <Alert onClose={() => setErrorMessage(null)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default AddManagerDialog;
